import { motion } from "framer-motion";
import { FaClipboardList, FaSignOutAlt, FaUserEdit } from "react-icons/fa";
import { FaChevronRight, FaRegCircleUser } from "react-icons/fa6";
import { RiAdminLine } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import SummaryApi from "../common";
import ROLE from "../common/role";
import {
  ACCOUNT_ADMIN_PANEL,
  ACCOUNT_ADMIN_PANEL_DESC,
  ACCOUNT_MY_ORDERS,
  ACCOUNT_MY_ORDERS_DESC,
  ACCOUNT_SETTINGS,
  ACCOUNT_SETTINGS_DESC,
  ACCOUNT_SIGN_OUT,
  ACCOUNT_SIGN_OUT_CONFIRM,
  ROUTES,
} from "../constants";
import { request } from "../helpers/request";
import { clearUserData } from "../store/userSlice";

const Account = () => {
  const user = useSelector((state) => state?.user?.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      const response = await request(SummaryApi.logout_user);
      const data = await response.json();
      if (data.success) {
        dispatch(clearUserData());
        // Auth cookies are HttpOnly and should be cleared by backend logout endpoint.
        toast.success(data.message);
        navigate(ROUTES.HOME);
      } else if (data.error) {
        toast.error(data.message);
      }
    } catch {
      toast.error(ACCOUNT_SIGN_OUT_CONFIRM);
    }
  };

  const menuItems = [
    {
      label: ACCOUNT_MY_ORDERS,
      icon: <FaClipboardList className="text-blue-500" />,
      path: ROUTES.ORDER,
      desc: ACCOUNT_MY_ORDERS_DESC,
      color: "bg-blue-50",
    },
    {
      label: ACCOUNT_SETTINGS,
      icon: <FaUserEdit className="text-purple-500" />,
      path: "/update-password",
      desc: ACCOUNT_SETTINGS_DESC,
      color: "bg-purple-50",
    },
  ];

  if (user?.role === ROLE.ADMIN) {
    menuItems.unshift({
      label: ACCOUNT_ADMIN_PANEL,
      icon: <RiAdminLine className="text-accent" />,
      path: "/admin-panel/dashboard",
      desc: ACCOUNT_ADMIN_PANEL_DESC,
      color: "bg-accent/5",
    });
  }

  return (
    <div className="bg-surface-muted min-h-screen py-8 md:py-12 pb-28 lg:pb-12">
      <div className="container mx-auto px-4 md:px-6 max-w-lg">
        {/* Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-white rounded-2xl md:rounded-3xl border border-surface-border shadow-card p-6 md:p-8 mb-5 text-center"
        >
          {/* Avatar */}
          <div className="relative w-20 h-20 md:w-24 md:h-24 mx-auto mb-4">
            <div className="w-full h-full rounded-full overflow-hidden border-4 border-surface-muted ring-2 ring-accent/10">
              {user?.profilePic ? (
                <img
                  src={user?.profilePic}
                  className="w-full h-full object-cover"
                  alt={user?.name}
                />
              ) : (
                <div className="w-full h-full bg-surface-muted flex items-center justify-center text-neutral-300">
                  <FaRegCircleUser className="text-4xl md:text-5xl" />
                </div>
              )}
            </div>
          </div>

          <h2 className="text-xl md:text-2xl font-black text-primary tracking-tight capitalize">
            {user?.name}
          </h2>
          <p className="text-sm text-neutral-400 font-medium mt-0.5 mb-3">
            {user?.email}
          </p>
          <span className="inline-flex items-center px-3 py-1 bg-accent/10 border border-accent/20 rounded-full text-[10px] font-black text-accent uppercase tracking-widest">
            {user?.role}
          </span>
        </motion.div>

        {/* Menu Items */}
        <div className="space-y-3">
          {menuItems.map((item, index) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.07 }}
            >
              <Link
                to={item.path}
                className="bg-white flex items-center gap-4 p-4 md:p-5 rounded-2xl border border-surface-border hover:shadow-card-hover hover:border-accent/20 transition-all duration-200 group"
              >
                <div
                  className={`w-11 h-11 rounded-xl ${item.color} flex items-center justify-center text-lg flex-shrink-0 transition-transform duration-200 group-hover:scale-110`}
                >
                  {item.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm md:text-base text-primary group-hover:text-accent transition-colors">
                    {item.label}
                  </p>
                  <p className="text-xs text-neutral-400 font-medium mt-0.5 truncate">
                    {item.desc}
                  </p>
                </div>
                <FaChevronRight className="text-xs text-neutral-300 group-hover:text-accent group-hover:translate-x-0.5 transition-all flex-shrink-0" />
              </Link>
            </motion.div>
          ))}

          {/* Sign Out */}
          <motion.button
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + menuItems.length * 0.07 }}
            onClick={handleLogout}
            className="w-full bg-white flex items-center gap-4 p-4 md:p-5 rounded-2xl border border-red-100 hover:bg-red-50 hover:border-red-200 transition-all duration-200 group mt-2"
          >
            <div className="w-11 h-11 rounded-xl bg-red-50 flex items-center justify-center text-red-500 flex-shrink-0 group-hover:scale-110 transition-transform">
              <FaSignOutAlt className="text-lg" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-bold text-sm md:text-base text-red-600">
                {ACCOUNT_SIGN_OUT}
              </p>
              <p className="text-xs text-red-300 font-medium mt-0.5">
                Securely log out of your account
              </p>
            </div>
          </motion.button>
        </div>
      </div>
    </div>
  );
};

export default Account;
